import React, { useState, useEffect, useRef } from 'react';
import { FiSend, FiPaperclip, FiX, FiUsers } from 'react-icons/fi';
import socketService from '../../services/socketService';
import chatService from '../../services/chatService';
import './ChatWindow.css';

const ChatWindow = ({ conversation, onClose, currentUser }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [typing, setTyping] = useState(false);
  const [typingUsers, setTypingUsers] = useState(new Set());
  const [newMessageIds, setNewMessageIds] = useState(new Set());
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  useEffect(() => {
    if (conversation?.id) {
      loadMessages();
      socketService.joinConversation(conversation.id);

      // Set up socket listeners
      socketService.onNewMessage(handleNewMessage);
      socketService.onUserTyping(handleUserTyping);
      socketService.onUserStoppedTyping(handleUserStoppedTyping);

      return () => {
        socketService.leaveConversation(conversation.id);
        socketService.removeListener('new_message');
        socketService.removeListener('user_typing');
        socketService.removeListener('user_stopped_typing');
      };
    }
  }, [conversation?.id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadMessages = async () => {
    try {
      setLoading(true);
      const data = await chatService.getMessages(conversation.id);
      setMessages(data.messages || []);
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNewMessage = (message) => {
    if (message.conversation_id === conversation.id) {
      setMessages(prev => [...prev, message]);
      
      // Mark message as new if not sent by current user
      if (message.sender_user_id !== currentUser?.id) {
        setNewMessageIds(prev => new Set([...prev, message.id]));
        
        // Remove bold state after 3 seconds
        setTimeout(() => {
          setNewMessageIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(message.id);
            return newSet;
          });
        }, 3000);
        
        // Mark as read
        socketService.markAsRead({
          conversationId: conversation.id,
          messageIds: [message.id]
        });
      }
    }
  };

  const handleUserTyping = ({ conversationId, userId }) => {
    if (conversationId === conversation.id && userId !== currentUser?.id) {
      setTypingUsers(prev => new Set([...prev, userId]));
    }
  };

  const handleUserStoppedTyping = ({ conversationId, userId }) => {
    if (conversationId === conversation.id) {
      setTypingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim()) return;

    const messageData = {
      conversationId: conversation.id,
      content: newMessage.trim(),
      messageType: 'text'
    };

    socketService.sendMessage(messageData);
    setNewMessage('');
    stopTyping();
  };

  const handleTyping = (e) => {
    setNewMessage(e.target.value);

    if (!typing) {
      setTyping(true);
      socketService.startTyping(conversation.id);
    }

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set new timeout to stop typing
    typingTimeoutRef.current = setTimeout(() => {
      stopTyping();
    }, 2000);
  };

  const stopTyping = () => {
    if (typing) {
      setTyping(false);
      socketService.stopTyping(conversation.id);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getParticipantName = (message) => {
    if (message.senderUser) {
      return `${message.senderUser.first_name} ${message.senderUser.last_name}`;
    }
    if (message.senderVendor) {
      return message.senderVendor.company_name;
    }
    return 'Unknown';
  };

  const isOwnMessage = (message) => {
    return message.sender_user_id === currentUser?.id;
  };

  return (
    <div className="chat-window">
      <div className="chat-header">
        <div className="chat-header-info">
          <h3>{conversation.title || 'Chat'}</h3>
          <div className="chat-participants">
            <FiUsers />
            <span>{conversation.participants?.length || 0} participants</span>
          </div>
        </div>
        <button className="chat-close-btn" onClick={onClose}>
          <FiX />
        </button>
      </div>

      <div className="chat-messages">
        {loading ? (
          <div className="chat-loading">Loading messages...</div>
        ) : messages.length === 0 ? (
          <div className="chat-empty">No messages yet. Start the conversation!</div>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className={`chat-message ${isOwnMessage(message) ? 'own-message' : 'other-message'} ${
                newMessageIds.has(message.id) ? 'new-message' : ''
              }`}
            >
              <div className="message-content">
                {!isOwnMessage(message) && (
                  <div className="message-sender">{getParticipantName(message)}</div>
                )}
                <div className="message-text">{message.content}</div>
                <div className="message-time">{formatTime(message.created_at)}</div>
              </div>
            </div>
          ))
        )}
        
        {typingUsers.size > 0 && (
          <div className="typing-indicator">
            <span>Someone is typing...</span>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      <form className="chat-input-form" onSubmit={handleSendMessage}>
        <button type="button" className="attach-btn" title="Attach file">
          <FiPaperclip />
        </button>
        <input
          type="text"
          className="chat-input"
          placeholder="Type a message..."
          value={newMessage}
          onChange={handleTyping}
          onBlur={stopTyping}
        />
        <button type="submit" className="send-btn" disabled={!newMessage.trim()}>
          <FiSend />
        </button>
      </form>
    </div>
  );
};

export default ChatWindow;


